import {
    ComponentType,
    FC,
    lazy,
    LazyExoticComponent,
    ReactNode,
    Suspense,
} from 'react';
import GlobalLoading from '@/components/elems/GlobalLoading/GlobalLoading.component';

export const lazyRetry = <T extends object>(
    componentImport: () => Promise<{ default: ComponentType<T> }>,
): LazyExoticComponent<ComponentType<T>> =>
    lazy<ComponentType<T>>(
        () =>
            new Promise((resolve, reject) => {
                const hasRefreshed = JSON.parse(
                    window.sessionStorage?.getItem('retry-lazy-refreshed') ||
                        'false',
                );

                componentImport()
                    .then((component) => {
                        window.sessionStorage?.setItem(
                            'retry-lazy-refreshed',
                            'false',
                        );
                        resolve(component);
                    })
                    .catch((error) => {
                        if (!hasRefreshed) {
                            window.sessionStorage?.setItem(
                                'retry-lazy-refreshed',
                                'true',
                            );
                            return window.location.reload();
                        }
                        reject(error);
                    });
            }),
    );

export const lazyRetryWithSuspense = <T extends object>(
    componentImport: () => Promise<{ default: ComponentType<T> }>,
    fallback?: ReactNode,
): FC<T> => {
    const LazyComponent = lazyRetry(componentImport);

    const WrappedComponent: FC<T> = (props: T) => (
        <Suspense fallback={fallback || <GlobalLoading />}>
            <LazyComponent {...props} />
        </Suspense>
    );

    WrappedComponent.displayName = 'LazyRetryWithSuspense';

    return WrappedComponent;
};
